<template>
  <div>
    <SpotlightCallout
        storage-key="sms-download-manager-intro-seen"
        title="New: Download Manager"
        ref="spotlightRef"
    >
        <template #content>
            <div class="space-y-1.5">
                <p>Exports like <strong>broadsheets</strong>, <strong>score sheets</strong>, and <strong>reports</strong> now generate in the background.</p>
                <p>Click the <i class="pi pi-download text-blue-500"></i> icon anytime to see progress, download files, or retry failed exports.</p>
                <p class="text-xs text-gray-400">You'll get a notification when each file is ready.</p>
            </div>
        </template>

        <div class="relative">
            <Button
                @click="togglePanel"
                icon="pi pi-download"
                text
                rounded
                class="!w-10 !h-10 text-gray-500 hover:bg-gray-100"
                v-tooltip.bottom="'Downloads'"
            />
            <span
                v-if="activeDownloadsCount > 0"
                class="dm-badge absolute top-2 right-2 w-3.5 h-3.5 bg-primary border-2 border-white rounded-full flex items-center justify-center text-[8px] text-white font-bold pointer-events-none"
            >{{ activeDownloadsCount }}</span>
        </div>
    </SpotlightCallout>

    <Popover ref="op" class="w-72 sm:w-80 p-0">
            <div class="flex flex-col max-h-[360px]">
                <!-- Header -->
                <div class="px-2.5 py-1.5 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <span class="font-semibold text-gray-700 text-sm">Downloads</span>
                    <div class="flex items-center gap-1">
                        <Button
                            icon="pi pi-question-circle"
                            text
                            rounded
                            size="small"
                            class="!p-1 text-gray-400 hover:text-blue-500"
                            @click="showHelp = !showHelp"
                            v-tooltip.left="'How it works'"
                        />
                        <Button
                            v-if="hasCompletedOrFailed"
                            label="Clear"
                            text
                            size="small"
                            class="!p-1 !px-2 text-xs text-gray-500 hover:text-red-600"
                            @click="clearFinished"
                            v-tooltip="'Clear completed & failed'"
                        />
                        <Button
                            icon="pi pi-refresh"
                            text
                            rounded
                            size="small"
                            class="!p-1 text-gray-500 hover:text-gray-700"
                            @click="refetch"
                            :loading="loading"
                        />
                    </div>
                </div>

                <!-- Collapsible help guide -->
                <Transition name="dm-help">
                    <div v-if="showHelp" class="px-3 py-2 border-b bg-blue-50/70 text-xs space-y-2">
                        <div class="flex items-start gap-2">
                            <span class="shrink-0 w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[9px] font-bold mt-0.5">1</span>
                            <div>
                                <span class="font-medium text-gray-600">Export a file</span>
                                <p class="text-gray-400 mt-0.5">Click "Export to Excel" on broadsheets, score sheets, or reports.</p>
                            </div>
                        </div>
                        <div class="flex items-start gap-2">
                            <span class="shrink-0 w-4 h-4 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[9px] font-bold mt-0.5">2</span>
                            <div>
                                <span class="font-medium text-gray-600">It generates in the background</span>
                                <p class="text-gray-400 mt-0.5">You can keep working while the file is being prepared.</p>
                            </div>
                        </div>
                        <div class="flex items-start gap-2">
                            <span class="shrink-0 w-4 h-4 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[9px] font-bold mt-0.5">3</span>
                            <div>
                                <span class="font-medium text-gray-600">Download when ready</span>
                                <p class="text-gray-400 mt-0.5">You'll get a notification. Come back here to download, retry, or clear files.</p>
                            </div>
                        </div>
                        <div class="pt-1.5 border-t border-blue-100 flex items-center gap-1.5 text-[10px] text-gray-400">
                            <i class="pi pi-info-circle"></i>
                            <span>Turn on <strong class="text-gray-500">Auto-download</strong> to skip step 3 &mdash; files download automatically.</span>
                        </div>
                    </div>
                </Transition>

                <!-- Auto-download toggle -->
                <div class="px-2.5 py-1.5 border-b bg-gray-50/50 flex items-center justify-between">
                    <label for="auto_dl" class="text-xs text-gray-600 font-medium cursor-pointer">Auto-download when ready</label>
                    <Checkbox v-model="autoDownload" inputId="auto_dl" :binary="true" />
                </div>

                <!-- List -->
                <div class="overflow-y-auto flex-1 p-0">
                    <!-- Loading skeleton -->
                    <div v-if="loading && exportsList.length === 0" class="p-5 text-center text-gray-400">
                        <i class="pi pi-spin pi-spinner text-lg mb-1.5 opacity-50"></i>
                        <span class="text-xs block">Loading...</span>
                    </div>

                    <!-- Empty state -->
                    <div v-else-if="exportsList.length === 0" class="p-5 text-center text-gray-400 flex flex-col items-center gap-1.5">
                        <i class="pi pi-inbox text-lg opacity-40"></i>
                        <span class="text-xs font-medium text-gray-500">No downloads yet</span>
                        <span class="text-xs opacity-70 max-w-[200px]">Exported files will appear here. Click <i class="pi pi-question-circle text-blue-400"></i> above to learn more.</span>
                    </div>

                    <!-- Export items -->
                    <div
                        v-else
                        v-for="exp in exportsList"
                        :key="exp.id"
                        class="px-2.5 py-2 border-b hover:bg-gray-50 transition-colors flex flex-col gap-1 relative"
                    >
                        <div class="flex items-start justify-between gap-2">
                            <!-- Status icon -->
                            <div class="mt-0.5 shrink-0">
                                <i v-if="exp.status === FileExportStatusEnum.Completed" class="pi pi-check-circle text-primary text-sm"></i>
                                <i v-else-if="exp.status === FileExportStatusEnum.Failed" class="pi pi-times-circle text-red-500 text-sm"></i>
                                <i v-else class="pi pi-spin pi-spinner text-blue-500 text-sm"></i>
                            </div>

                            <!-- Content -->
                            <div class="flex-1 min-w-0">
                                <p class="text-xs text-gray-800 truncate font-medium">
                                    {{ exp.file_name || formatTypeName(exp.type) || 'Export' }}
                                </p>
                                <div class="text-[10px] text-gray-400 mt-0.5 flex items-center gap-2">
                                    <span :class="statusClass(exp.status)">{{ formatStatus(exp.status) }}</span>
                                    <span v-if="exp.status === FileExportStatusEnum.Completed && exp.file_size" class="text-gray-300">
                                        {{ formatFileSize(exp.file_size) }}
                                    </span>
                                    <span class="ml-auto">{{ formatDate(exp.created_at) }}</span>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div class="shrink-0 flex items-center gap-0.5">
                                <Button
                                    v-if="exp.status === FileExportStatusEnum.Completed"
                                    icon="pi pi-download"
                                    text
                                    rounded
                                    size="small"
                                    class="!p-1 text-primary hover:bg-primary/10"
                                    @click="downloadFile(exp)"
                                    v-tooltip.left="'Download'"
                                />
                                <Button
                                    v-if="exp.status === FileExportStatusEnum.Failed"
                                    icon="pi pi-refresh"
                                    text
                                    rounded
                                    size="small"
                                    class="!p-1 text-orange-600 hover:bg-orange-50"
                                    @click="retryExport(exp)"
                                    :loading="retryingId === exp.id"
                                    v-tooltip.left="'Retry'"
                                />
                                <Button
                                    v-if="exp.status === FileExportStatusEnum.Failed"
                                    icon="pi pi-info-circle"
                                    text
                                    rounded
                                    size="small"
                                    class="!p-1 text-red-400 hover:bg-red-50"
                                    v-tooltip.left="truncateError(exp.error_message)"
                                />
                                <Button
                                    v-if="exp.status === FileExportStatusEnum.Completed || exp.status === FileExportStatusEnum.Failed"
                                    icon="pi pi-times"
                                    text
                                    rounded
                                    size="small"
                                    class="!p-1 text-gray-300 hover:text-red-500 hover:bg-red-50"
                                    @click="deleteExport(exp)"
                                    :loading="deletingId === exp.id"
                                    v-tooltip.left="'Remove'"
                                />
                            </div>
                        </div>

                        <!-- Progress Bar -->
                        <div v-if="exp.status === FileExportStatusEnum.Processing || exp.status === FileExportStatusEnum.Pending" class="w-full bg-gray-200 rounded-full h-1.5">
                            <div
                                class="h-1.5 rounded-full transition-all duration-300"
                                :class="exp.progress > 0 ? 'bg-blue-500' : 'bg-blue-400 dm-progress-indeterminate'"
                                :style="exp.progress > 0 ? { width: `${exp.progress}%` } : { width: '40%' }"
                            ></div>
                        </div>

                        <!-- Stale warning with direct download fallback -->
                        <div v-if="isStale(exp)" class="flex items-center justify-between gap-1.5 text-[10px] text-amber-600 bg-amber-50 rounded px-1.5 py-1">
                            <div class="flex items-center gap-1">
                                <i class="pi pi-exclamation-triangle text-[10px]"></i>
                                <span>Taking longer than expected.</span>
                            </div>
                            <button
                                class="text-[10px] font-semibold text-blue-600 hover:text-blue-800 underline whitespace-nowrap disabled:opacity-50"
                                :disabled="processingNowId === exp.id"
                                @click="processNow(exp)"
                            >
                                {{ processingNowId === exp.id ? 'Generating...' : 'Download directly' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Popover>
  </div>
</template>

<script lang="ts">
// These guards are intentionally module-scoped so they are SHARED across every
// mounted DownloadManager instance in the tab. The admin dashboard renders the
// widget in both the desktop header (AdminNavbar) and the mobile header, and
// those headers are toggled with CSS (`hidden`/`lg:hidden`) — which only hides
// them, it never unmounts them. So two instances are always alive at once, each
// with its own export subscription. A per-instance guard therefore can't stop
// the second instance from auto-downloading the same file (double download) or
// raising a duplicate toast. Sharing the guards at module scope makes the whole
// tab agree that a given export has already been handled.
const autoDownloadedIds = new Set<string>();
const notifiedExportKeys = new Set<string>();

// Module scope only dedupes within ONE tab — every open tab has its own
// subscription and used to auto-download the same file once each. The claims
// registry in localStorage extends the guard across tabs: an export id is
// auto-downloaded by whichever context claims it first, everyone else skips.
// Claims expire after a day so the key can't grow unbounded (auto-download
// only ever fires on a live status transition, so old claims are dead weight).
// Toasts stay per-tab on purpose — the user may be looking at any tab.
const AUTO_DOWNLOAD_CLAIMS_KEY = 'sms-auto-downloaded-exports';
const AUTO_DOWNLOAD_CLAIM_TTL_MS = 24 * 60 * 60 * 1000;

const readClaims = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(AUTO_DOWNLOAD_CLAIMS_KEY) || '{}') as Record<string, number>;
  } catch {
    return {};
  }
};

const hasClaim = (id: string) => autoDownloadedIds.has(id) || readClaims()[id] !== undefined;

const recordClaim = (id: string) => {
  autoDownloadedIds.add(id);
  try {
    const cutoff = Date.now() - AUTO_DOWNLOAD_CLAIM_TTL_MS;
    const claims = Object.fromEntries(
      Object.entries(readClaims()).filter(([, claimedAt]) => claimedAt > cutoff),
    );
    claims[id] = Date.now();
    localStorage.setItem(AUTO_DOWNLOAD_CLAIMS_KEY, JSON.stringify(claims));
  } catch {
    // Storage unavailable (quota, private mode) — degrade to per-tab dedupe.
  }
};

/**
 * Atomically claim an export for auto-download. Returns true for exactly one
 * caller per export across all tabs. Two tabs get the completion broadcast
 * within the same millisecond, and localStorage has no compare-and-swap, so
 * the check+write runs inside a Web Lock where the browser supports it; the
 * synchronous fallback still closes the gap within a single tab.
 */
const claimAutoDownload = async(id: string): Promise<boolean> => {
  if (hasClaim(id)) return false;
  if (typeof navigator !== 'undefined' && navigator.locks) {
    return await navigator.locks.request(AUTO_DOWNLOAD_CLAIMS_KEY, async() => {
      if (hasClaim(id)) return false;
      recordClaim(id);
      return true;
    });
  }
  recordClaim(id);
  return true;
};
</script>

<script setup lang="ts">
import SpotlightCallout from './SpotlightCallout.vue';
import { formatDistanceToNow } from 'date-fns';
import { useUserStore } from '@/store/userStore';
import { useGetFileExportsQuery, useExportStatusUpdatedSubscription, useDeleteExportMutation, useRetryExportMutation, useProcessExportNowMutation } from '@/types/vue-apollo-helpers';
import { FileExportStatusEnum, type FileExport } from '@/types/composition-functions';
import { useToast } from 'primevue/usetoast';
import { useLocalStorage } from '@vueuse/core';

const op = ref();
const spotlightRef = ref<InstanceType<typeof SpotlightCallout>>();
const toast = useToast();
const userStore = useUserStore();

const showHelp = ref(false);

// Default to ON for every school; turning it off is a per-user choice that sticks.
// v2 key: useLocalStorage writes the default on first visit, so under the old key
// an untouched browser already stores "false" — indistinguishable from a real
// opt-out. Renaming the key is the only way to re-default existing browsers to ON
// while still honouring opt-outs made from here on.
const autoDownload = useLocalStorage('sms-auto-download-exports-v2', true);
// The pre-v2 key is never read again — drop it so it doesn't sit in
// localStorage forever. Done in setup (not module scope) so it only runs
// when the component actually mounts.
localStorage.removeItem('sms-auto-download-exports');

const retryingId = ref<string | null>(null);
const deletingId = ref<string | null>(null);
const processingNowId = ref<string | null>(null);
const localExportsList = ref<FileExport[]>([]);

// Fire a toast at most once per export transition. Both the query (poll) and
// subscription paths — across both mounted instances — funnel through here, so
// the shared `notifiedExportKeys` guard collapses them to a single toast.
const notifyOnce = (key: string, message: Parameters<typeof toast.add>[0]) => {
  if (notifiedExportKeys.has(key)) return;
  notifiedExportKeys.add(key);
  toast.add(message);
};

const togglePanel = (event: Event) => {
  const wasVisible = op.value?.visible;
  op.value.toggle(event);
  if (!wasVisible) {
    refetch();
  }
};

// --- QUERY ---
const { result: queryResult, loading, refetch: refetchQuery } = useGetFileExportsQuery(
  { first: 10, page: 1 },
  { fetchPolicy: 'network-only' },
);

watch(queryResult, async(newVal) => {
  if (newVal?.fileExports?.data) {
    const freshList = newVal.fileExports.data as FileExport[];

    // Snapshot before replacing the list: the claim below is async, and the
    // subscription watcher may touch localExportsList while we're suspended.
    const prevList = localExportsList.value;
    localExportsList.value = [...freshList];

    // Auto-download exports that just became completed (detected via polling)
    if (autoDownload.value) {
      for (const exp of freshList) {
        if (exp.status !== FileExportStatusEnum.Completed) continue;
        const prev = prevList.find(e => e.id === exp.id);
        if (prev && prev.status !== FileExportStatusEnum.Completed) {
          const claimed = await claimAutoDownload(exp.id);
          if (claimed) downloadFile(exp);
          notifyOnce(`${exp.id}:completed`, {
            severity: 'success',
            summary: 'Export Ready',
            detail: `${exp.file_name || exp.type} is ready. ${claimed ? 'Downloading...' : 'Downloaded in another tab.'}`,
            life: 5000,
          });
        }
      }
    }
  }
}, { immediate: true });

const refetch = async() => {
  await refetchQuery();
};

// --- SUBSCRIPTION ---
const { result: subResult } = useExportStatusUpdatedSubscription(
  () => ({ user_id: userStore.currentUser?.id as string }),
  () => ({ enabled: !!userStore.currentUser?.id }),
);

watch(subResult, async(newVal) => {
  if (newVal?.exportStatusUpdated) {
    const updatedExport = newVal.exportStatusUpdated as FileExport;

    const index = localExportsList.value.findIndex(e => e.id === updatedExport.id);
    const oldStatus = index !== -1 ? localExportsList.value[index]?.status : null;

    if (index !== -1) {
      localExportsList.value[index] = { ...localExportsList.value[index]!, ...updatedExport };
    } else {
      localExportsList.value.unshift(updatedExport);
      if (localExportsList.value.length > 20) {
        localExportsList.value.pop();
      }
    }

    if (updatedExport.status === FileExportStatusEnum.Completed && oldStatus !== FileExportStatusEnum.Completed) {
      const claimed = autoDownload.value && await claimAutoDownload(updatedExport.id);
      notifyOnce(`${updatedExport.id}:completed`, {
        severity: 'success',
        summary: 'Export Ready',
        detail: `${updatedExport.file_name || updatedExport.type} is ready. ${
          claimed ? 'Downloading...' : autoDownload.value ? 'Downloaded in another tab.' : 'Open Downloads to get it.'}`,
        life: 5000,
      });

      if (claimed) downloadFile(updatedExport);
    }

    if (updatedExport.status === FileExportStatusEnum.Failed && oldStatus !== FileExportStatusEnum.Failed) {
      notifyOnce(`${updatedExport.id}:failed`, {
        severity: 'error',
        summary: 'Export Failed',
        detail: `${updatedExport.file_name || updatedExport.type} failed. Open Downloads to retry.`,
        life: 5000,
      });
    }
  }
});

// --- MUTATIONS ---
const { mutate: deleteExportMutation } = useDeleteExportMutation();
const { mutate: retryExportMutation } = useRetryExportMutation();
const { mutate: processExportNowMutation } = useProcessExportNowMutation();

// --- COMPUTEDS ---
const exportsList = computed(() => localExportsList.value || []);

const activeDownloadsCount = computed(() =>
  exportsList.value.filter(e => e.status === FileExportStatusEnum.Processing || e.status === FileExportStatusEnum.Pending).length,
);

// --- POLLING ---
// Poll whenever an export is active, regardless of WebSocket health. The
// subscription is an accelerator, not a dependency: if Reverb is down, never
// connected at all (bad env/build), or a broadcast is dropped, polling still
// converges the list. Cheap — runs only while something is pending, and the
// query is 10 rows. The previous version polled only after an explicit
// Pusher "disconnected" event, which never fires when the socket fails to
// connect in the first place — exports then sat "Processing" on screen
// forever even after the server finished or failed them.
let pollInterval: ReturnType<typeof setInterval> | null = null;

const shouldPoll = computed(() => activeDownloadsCount.value > 0);

watch(shouldPoll, (poll) => {
  if (poll && !pollInterval) {
    pollInterval = setInterval(() => refetch(), 15_000);
  } else if (!poll && pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
}, { immediate: true });

// Catch up as soon as the user returns to the tab: intervals are throttled
// in background tabs, and a machine waking from sleep has missed both
// broadcasts and polls.
const onVisibilityChange = () => {
  if (document.visibilityState === 'visible' && activeDownloadsCount.value > 0) {
    refetch();
  }
};
document.addEventListener('visibilitychange', onVisibilityChange);

onBeforeUnmount(() => {
  if (pollInterval) clearInterval(pollInterval);
  document.removeEventListener('visibilitychange', onVisibilityChange);
});

const hasCompletedOrFailed = computed(() =>
  exportsList.value.some(e => e.status === FileExportStatusEnum.Completed || e.status === FileExportStatusEnum.Failed),
);

// --- HELPERS ---
const formatDate = (date: string) => {
  try {
    return formatDistanceToNow(new Date(date), { addSuffix: true });
  } catch {
    return date;
  }
};

const formatStatus = (status: string) => {
  switch (status) {
  case FileExportStatusEnum.Pending: return 'Queued';
  case FileExportStatusEnum.Processing: return 'Processing...';
  case FileExportStatusEnum.Completed: return 'Ready';
  case FileExportStatusEnum.Failed: return 'Failed';
  default: return status;
  }
};

const statusClass = (status: string) => {
  switch (status) {
  case FileExportStatusEnum.Completed: return 'text-primary';
  case FileExportStatusEnum.Failed: return 'text-red-500';
  case FileExportStatusEnum.Processing: return 'text-blue-500';
  default: return 'text-gray-400';
  }
};

const formatTypeName = (type: string) => {
  if (!type) return '';
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const truncateError = (msg?: string | null) => {
  if (!msg) return 'Export failed. Try again.';
  return msg.length > 120 ? msg.substring(0, 120) + '...' : msg;
};

const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

const isStale = (exp: FileExport) => {
  if (exp.status !== FileExportStatusEnum.Pending && exp.status !== FileExportStatusEnum.Processing) return false;
  const created = new Date(exp.created_at).getTime();
  return Date.now() - created > STALE_THRESHOLD_MS;
};

/**
 * Download using fetch + blob to avoid:
 * 1. Cross-origin URLs causing new tabs (browser ignores `download` attr)
 * 2. `[object Object]` if file_url is accidentally an object
 */
const downloadFile = async(exp: FileExport) => {
  const url = typeof exp.file_url === 'string' ? exp.file_url : String(exp.file_url ?? '');
  if (!url || url === 'null' || url === 'undefined') return;

  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = exp.file_name || 'download';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    // Fallback: open in new tab
    window.open(url, '_blank');
  }
};

const deleteExport = async(exp: FileExport) => {
  deletingId.value = exp.id;
  try {
    await deleteExportMutation({ id: exp.id });
    localExportsList.value = localExportsList.value.filter(e => e.id !== exp.id);
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to delete export', life: 3000 });
  } finally {
    deletingId.value = null;
  }
};

const retryExport = async(exp: FileExport) => {
  retryingId.value = exp.id;
  try {
    const result = await retryExportMutation({ id: exp.id });
    if (result?.data?.retryExport) {
      const index = localExportsList.value.findIndex(e => e.id === exp.id);
      if (index !== -1) {
        localExportsList.value[index] = { ...localExportsList.value[index]!, ...result.data.retryExport };
      }
    }
    toast.add({ severity: 'success', summary: 'Retrying', detail: 'Export re-queued.', life: 3000 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to retry export', life: 3000 });
  } finally {
    retryingId.value = null;
  }
};

/**
 * Fallback: generate the file synchronously on the server and download it.
 * Used when the queue worker isn't processing jobs.
 */
const processNow = async(exp: FileExport) => {
  processingNowId.value = exp.id;
  try {
    const result = await processExportNowMutation({ id: exp.id });
    const updated = result?.data?.processExportNow;
    if (updated) {
      const index = localExportsList.value.findIndex(e => e.id === exp.id);
      if (index !== -1) {
        localExportsList.value[index] = { ...localExportsList.value[index]!, ...updated };
      }
      // The synchronous run broadcasts exportStatusUpdated before this
      // mutation resolves, so the subscription/poll watchers (in this
      // instance, its CSS-hidden twin, or another tab) may already be
      // downloading the file — share their claim or the user gets it twice.
      if (updated.status === FileExportStatusEnum.Completed && await claimAutoDownload(updated.id)) {
        toast.add({ severity: 'success', summary: 'Export Ready', detail: 'File generated. Downloading...', life: 3000 });
        downloadFile(updated);
      }
    }
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to process export', life: 5000 });
  } finally {
    processingNowId.value = null;
  }
};

const clearFinished = async() => {
  const finished = localExportsList.value.filter(e => e.status === FileExportStatusEnum.Completed || e.status === FileExportStatusEnum.Failed);
  for (const exp of finished) {
    try {
      await deleteExportMutation({ id: exp.id });
    } catch {
      // skip individual failures
    }
  }
  localExportsList.value = localExportsList.value.filter(
    e => e.status !== FileExportStatusEnum.Completed && e.status !== FileExportStatusEnum.Failed,
  );
};
</script>

<style scoped>
/* Badge entrance animation */
.dm-badge {
    animation: dm-badge-pop 0.3s ease-out;
}

@keyframes dm-badge-pop {
    0% { transform: scale(0); }
    70% { transform: scale(1.2); }
    100% { transform: scale(1); }
}

/* Indeterminate progress bar for pending (0%) state */
.dm-progress-indeterminate {
    animation: dm-indeterminate 1.5s ease-in-out infinite;
}

@keyframes dm-indeterminate {
    0% { margin-left: 0; width: 30%; }
    50% { margin-left: 40%; width: 40%; }
    100% { margin-left: 0; width: 30%; }
}

/* Help panel slide transition */
.dm-help-enter-active { animation: dm-help-slide 0.2s ease-out; }
.dm-help-leave-active { animation: dm-help-slide 0.15s ease-in reverse; }

@keyframes dm-help-slide {
    0% { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
    100% { opacity: 1; max-height: 300px; }
}
</style>
